import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { MitIdSigneringRequest } from 'src/app/api/models';
import { SigneringService } from 'src/app/api/services';

@Component({
  selector: 'bom-digital-signering-mitid',
  templateUrl: './digital-signering-mitid.component.html',
  styleUrls: ['./digital-signering-mitid.component.scss'],
})
export class DigitalSigneringMitidComponent implements OnInit, OnDestroy {
  errorMessage = new BehaviorSubject<string>(null);
  ticket: string;
  encodedTicket: string;
  mitIdSigneringRequest: MitIdSigneringRequest;
  safeSigningClientUrl: SafeResourceUrl;
  showSpinner: boolean;
  spinnerText: string;

  private onSigningClientMessageBound: (event: any) => void;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private signeringService: SigneringService
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(take(1)).subscribe(params => {
      this.encodedTicket = encodeURIComponent(params.ticket);
      this.ticket = params.ticket;

      this.showSpinner = true;
      this.spinnerText = 'Henter dokument til signering...';

      this.signeringService.getDigitalSigneringRequestMitId(this.ticket).subscribe({
        next: mitIdSigneringRequest => {
          this.mitIdSigneringRequest = mitIdSigneringRequest;
          this.safeSigningClientUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            mitIdSigneringRequest.signingClientUrl
          );
          this.showSpinner = false;
          this.spinnerText = '';

          this.setupEventListener();
          this.errorMessage.next(null);
        },
        error: e => {
          if (e.status === 400) {
            this.router.navigate(['digitalsignering', this.ticket]);
          } else {
            this.router.navigate(['digitalsignering/kvittering'], {
              queryParams: { errorKey: 'DigitalSigneringInternFejlKvittering' },
            });
          }
        },
      });
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onSigningClientMessageBound, false);
  }

  setupEventListener() {
    this.onSigningClientMessageBound = this.onSigningClientMessage.bind(this);

    window.addEventListener('message', this.onSigningClientMessageBound, false);
  }

  onSigningClientMessage(event: any) {
    // Set message from SigningClient
    const message = event.data;

    // OWASP HTML5 Security - must protect calling frame unsafe web messaging
    const origin = this.mitIdSigneringRequest.signingClientUrl;
    if (!origin.startsWith(event.origin)) {
      console.error('SP: event ' + event.origin + ' origin rejected');
      return;
    }

    switch (message.command) {
      case SigningClientCommand.SendParameters:
        this.onSendParameters();
        break;
      case SigningClientCommand.SignedDocument:
        this.onSignedDocument(message.content);
        break;
      case SigningClientCommand.ErrorResponse:
        this.onErrorResponse(message.content);
        break;
      case SigningClientCommand.CancelSign:
        this.onCancelSign();
        break;
    }
  }

  onSendParameters() {
    const params = {
      command: SigningClientCommand.Parameters,
      content: this.mitIdSigneringRequest,
    };

    const window = (document.getElementById('signing-window') as HTMLIFrameElement).contentWindow;
    window.postMessage(params, '*');
  }

  onSignedDocument(content: string) {
    this.showSpinner = true;
    this.spinnerText = 'Validerer signering...';

    this.signeringService
      .validerDigitalSigneringMitId({ ticket: this.ticket, documentData: content })
      .subscribe({
        next: validerSigneringResult => {
          console.log(validerSigneringResult);
          if (validerSigneringResult.eksternFejl) {
            this.router.navigate(['digitalsignering/kvittering'], {
              queryParams: { eksternFejl: true },
            });
          } else if (validerSigneringResult.pseudonym) {
            this.router.navigate(['digitalsignering/kvittering'], {
              queryParams: { pseudonym: true },
            });
          } else {
            this.router.navigate(['digitalsignering/kvittering']);
          }
        },
        error: e => {
          this.showSpinner = false;
          this.spinnerText = '';

          if (e.status === 400) {
            const errors = e.error;
            this.errorMessage.next(errors);
          } else {
            this.router.navigate(['digitalsignering/kvittering'], {
              queryParams: { errorKey: 'DigitalSigneringInternFejlKvittering' },
            });
          }
        },
      });
  }

  onErrorResponse(content: any) {
    const jsonError = atob(content);
    const error = JSON.parse(jsonError) as SigningError;
    const errorDetails = error.details.map(d => `${d.errorCode}: ${d.errorMessage}`);
    const errorMessage = `${error.message}. Details: \n${errorDetails.join('\n')}`;
    throw new Error(errorMessage);
  }

  onCancelSign() {
    this.router.navigate(['digitalsignering', this.ticket]);
  }
}

enum SigningClientCommand {
  SendParameters = 'SendParameters',
  Parameters = 'parameters',
  ErrorResponse = 'errorResponse',
  CancelSign = 'cancelSign',
  SignedDocument = 'signedDocument',
}

interface SigningError {
  httpStatusCode: number;
  timestamp: string;
  message: string;
  details: SigningErrorDetail[];
}

interface SigningErrorDetail {
  errorCode: string;
  errorMessage: string;
}
